






















import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Subscription, Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { FormFieldConfig } from '@shared/interfaces/config';
import { ValidationObserver } from 'vee-validate';
import { ApiRouteType } from '@core/interfaces/core-services';

@Component({
  name: 'bpc-form-provider',
  components: {
    'validation-observer': ValidationObserver,
  },
})
export default class FormProvider<T = AnyEntity> extends Vue {
  @Prop(String) readonly entity: string;
  @Prop([String, Number]) readonly entityId: string | number;
  @Prop(String) readonly savePath: string;
  @Prop(String) readonly searchPath: string;
  @Prop(String) readonly configPath: string;
  @Prop(Boolean) readonly fetchEntity: boolean;
  @Prop(String) readonly fetchPath: string;
  @Prop(Boolean) readonly hasMedia: boolean;
  @Prop({ type: Boolean, default: true }) readonly useModelFactory: boolean;
  @Prop(Boolean) readonly reloadOnEntityIdChange: boolean;

  @Prop(Object) readonly value: T;

  @Prop({ type: Boolean, default: false }) readonly forcePutMethod: boolean;

  @Prop({ type: String, default: null }) readonly apiRouteType: ApiRouteType;

  @Prop({ type: String, default: 'global.generic.successful_action' })
  readonly createdSuccessText: string;
  @Prop({ type: String, default: 'global.generic.successful_action' })
  readonly updatedSuccessText: string;
  @Prop({ type: String, default: 'global.generic.error_form_action' })
  readonly errorMessageText: string;
  @Prop({ type: Object, default: () => ({}) })
  readonly fieldsObject: Record<string, FormFieldConfig>;
  @Prop({ type: Object, default: () => ({}) }) readonly extraData: Partial<T>;
  @Prop({
    type: Function,
    default: (entityCopy: T): T => {
      return entityCopy;
    },
  })
  readonly prepareToSave: (entityCopy: T) => T;

  loading = false;
  saving = false;

  state = null;

  entitySubscription: Subscription;
  saveEntitySubscription: Subscription;
  saveEntityObservable: Observable<BPCGenericApiResponse>;

  errorsObject: object = {};

  public get fields(): FormFieldConfig[] {
    if (!this.fieldsObject || Object.keys(this.fieldsObject).length === 0) {
      return [];
    }
    return Object.values(this.fieldsObject);
  }

  public get model(): T {
    return this.value;
  }

  public set model(val: T) {
    this.$emit('input', val);
  }

  public get fetchEntityPath() {
    if (this.fetchPath && this.entityId) {
      return this.fetchPath;
    } else if (this.entityId) {
      return `${this.$bpcApiRouteHelper.getEntityPath(this.entity, 'raw')}/${this.entityId}`;
    }

    return this.$bpcApiRouteHelper.getEntityPath(this.entity, 'raw');
  }

  public get saveEntityPath(): string {
    if (this.savePath) {
      return this.savePath;
    } else if (this.entityId) {
      return `${this.$bpcApiRouteHelper.getEntityPath(this.entity)}/${this.entityId}`;
    }

    return this.$bpcApiRouteHelper.getEntityPath(this.entity);
  }

  public get searchEntityPath(): string {
    if (this.searchPath) {
      return this.searchPath;
    }
    return `${this.$bpcApiRouteHelper.getSearchBookingPath(this.entity)}/search`;
  }

  public async created(): Promise<void> {
    if (this.fetchEntity) {
      this.getEntity();
    }
  }

  public async getEntity(): Promise<void> {
    this.loading = true;

    if (this.entitySubscription) {
      this.entitySubscription.unsubscribe();
    }

    const enityObservable = this.getEntityObservable();
    this.entitySubscription = enityObservable.subscribe(
      async (res: BPCGenericApiResponse) => {
        if (this.useModelFactory) {
          this.model = this.$bpcModelFactory.getInitialFormModel(
            this.fields,
            this.entityId,
            res.data,
            this.extraData,
          );
        } else {
          this.model = res.data;
        }

        this.loading = false;
        await this.$nextTick();
        this.$emit('model-loaded', this.model);
      },
      error => {
        this.loading = false;
      },
    );
  }

  getEntityObservable() {
    return this.$bpcApi.get(this.fetchEntityPath);
  }

  public async saveEntity(entity: AnyEntity): Promise<BPCGenericApiResponse> {
    return new Promise((resolve, reject) => {
      this.saving = true;
      let successText = null;

      if (this.saveEntitySubscription) {
        this.saveEntitySubscription.unsubscribe();
      }
      let entityCopy = { ...entity };
      entityCopy = this.prepareToSave(entityCopy);

      if (this.entityId || this.forcePutMethod) {
        successText = this.updatedSuccessText;
        if (this.hasMedia) {
          const formData = this.convertModelToFormData(entityCopy, 'put');
          this.saveEntityObservable = this.$bpcApi.putWithMedia(this.saveEntityPath, formData);
        } else {
          this.saveEntityObservable = this.$bpcApi.put(
            this.saveEntityPath,
            entityCopy,
            null,
            this.apiRouteType,
          );
        }
      } else {
        successText = this.createdSuccessText;
        if (this.hasMedia) {
          const formData = this.convertModelToFormData(entityCopy);
          this.saveEntityObservable = this.$bpcApi.postWithMedia(this.saveEntityPath, formData);
        } else {
          this.saveEntityObservable = this.$bpcApi.post(
            this.saveEntityPath,
            entityCopy,
            null,
            this.apiRouteType,
          );
        }
      }

      this.saveEntitySubscription = this.saveEntityObservable.subscribe(
        (res: BPCGenericApiResponse) => {
          this.$bpcMessage.showSuccessMessage({ text: successText });
          this.$emit('entity-saved', res);
          this.saving = false;
          resolve(res);
        },
        error => {
          // NOTE not sure what to do, this is managed in the interceptor
          // this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
          this.resetErrorObject();
          this.manageSaveErrors(error);
          this.$emit('entity-error-saving', error);
          this.saving = false;
          reject(error);
        },
      );
    });
  }

  resetErrorObject() {
    this.errorsObject = {};
  }

  manageSaveErrors(error) {
    if (error.response.status === 422) {
      if (error.response.data.errors) {
        // this.errorsObject = error.response.data.errors;
        Object.keys(error.response.data.errors).forEach(field => {
          const errorField = {
            error: error.response.data.errors[field][0],
            state: false,
            veeValidation: false,
          };
          this.$set(this.errorsObject, field, errorField);
        });
      }
    } else if (error.response.status === 420) {
      this.$emit('validation-warning', error);
    }
  }

  public restartState(field) {
    if (this.errorsObject[field]) {
      this.$set(this.errorsObject[field], 'error', null);
      this.$set(this.errorsObject[field], 'state', false);
      this.$set(this.errorsObject[field], 'veeValidation', false);
    }
  }

  protected convertModelToFormData(model: any, method: string = null) {
    const formData = new FormData();
    if (method === 'put') {
      formData.append('_method', method);
    }
    return this.buildFormData(model, formData);
  }

  protected buildFormData(data, formData: FormData, parentKey: string = '') {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
      Object.keys(data).forEach(key => {
        formData = this.buildFormData(
          data[key],
          formData,
          parentKey ? `${parentKey}[${key}]` : key,
        );
      });
    } else {
      const value = data === null ? '' : data;
      formData.append(parentKey, value);
    }
    return formData;
  }

  @Watch('saving')
  onSavingChange() {
    this.$emit('saving-entity', this.saving);
  }

  @Watch('entityId')
  onEntityIdChange() {
    if (this.reloadOnEntityIdChange) {
      this.getEntity();
    }
  }
}
